import { Box, CardContent, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useNotification } from '@revenuewell/front-end-bundle';
import { NoDataComponent } from 'apps/wrapper/src/components/shared/grid/no-data';
import { AddNoteDialog } from 'apps/wrapper/src/components/shared/notes/add-note/add-note';
import { usePatientProfile } from 'apps/wrapper/src/hooks/use-patient-profile';
import { ContactApi } from 'apps/wrapper/src/services/api-client/contact-api';
import { PatientNote } from 'apps/wrapper/src/services/api-client/contact-api.types';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, CardTitle, Table } from '../../patient-profile-page.styles';
import NotesTableSkeleton from './notes.skeleton';
import { Container } from './notes.styles';

export const NotesView: FC = () => {
  const { showError } = useNotification();
  const { contactDetails, locationId } = usePatientProfile();
  const [notes, setNotes] = useState<PatientNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedNote, setSelectedNote] = useState<PatientNote>();
  const [noteDialogOpened, setNoteDialogOpened] = useState(false);
  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = useCallback(async () => {
    setIsLoading(true);

    try {
      const apiNotes = await contactApi.getNotes(contactDetails.id, locationId);

      setNotes(apiNotes);
    } catch {
      showError('Unable to load notes.');
    } finally {
      setIsLoading(false);
    }
  }, [contactDetails, locationId]);

  const closeDialog = useCallback(() => {
    setNoteDialogOpened(false);
    setSelectedNote(undefined);
  }, []);

  const handleNoteClick = useCallback((note: PatientNote) => {
    setSelectedNote(note);
    setNoteDialogOpened(true);
  }, []);

  const handleAddNoteClick = useCallback(() => {
    setNoteDialogOpened(true);
  }, []);

  const handleNoteUpdate = useCallback(
    (note: PatientNote) => {
      const updatedNotes = notes.map((n) => (n.id === note.id ? note : n));

      setNotes(updatedNotes);
      closeDialog();
    },
    [notes, closeDialog]
  );

  const handleNoteAdd = useCallback(() => {
    loadNotes();
    closeDialog();
  }, [loadNotes]);

  return (
    <>
      <Container>
        <Card>
          <CardContent>
            <CardTitle sx={{ display: 'flex', justifyContent: 'space-between', padding: '10px', width: 'auto' }}>
              <Box>Patient Notes</Box>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Button size="small" onClick={handleAddNoteClick} variant="contained" disableElevation>
                  Add Note
                </Button>
              </Box>
            </CardTitle>
            {isLoading && <NotesTableSkeleton />}
            {!isLoading && notes.length === 0 && <NoDataComponent />}
            {!isLoading && notes.length > 0 && (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '15%' }}>Date</TableCell>
                    <TableCell>Response</TableCell>
                    <TableCell sx={{ width: '15%' }}>Author</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notes.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell style={{ width: '15%', paddingRight: '15px', color: 'black' }}>
                        {item.date ? moment(item.date).format('MM/DD/YYYY') : null}
                      </TableCell>
                      <TableCell onClick={() => handleNoteClick(item)} style={{ paddingRight: '15px' }}>
                        <Box sx={{ color: '#0675DD', '&:hover': { cursor: 'pointer', textDecoration: 'underline' } }}>
                          {item.note}
                        </Box>
                      </TableCell>
                      <TableCell style={{ width: '15%', color: 'black' }}>{item.authorFullName}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Container>
      <AddNoteDialog
        note={selectedNote}
        opened={noteDialogOpened}
        onClose={closeDialog}
        onAddNode={handleNoteAdd}
        onUpdateNote={handleNoteUpdate}
      />
    </>
  );
};
